import React, { useState, useEffect, useRef } from 'react';
import { Grid, FileText, ZoomIn, ZoomOut } from 'lucide-react';
import { usePdfSearch, PdfSearchControl, PdfPageHighlights } from '../pdfSearch';
import * as XLSX from 'xlsx';
import ExcelViewer from '../ExcelViewer';
import ExcelViewerFormatted from '../ExcelViewerFormatted';
import { parseJsonViewerContent } from '../../utils/jsonViewer';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * Inline file viewer panel (no modal/portal). Renders PDF, Excel, CSV, text, HTML, JSON.
 * Props:
 *   url        — ready-to-fetch URL (local object URL or direct URL)
 *   blobUrl    — raw Azure blob URL that needs proxying via fetchBlobViaProxy
 *   fileType   — 'pdf'|'excel'|'csv'|'text'|'html'|'json'
 *   className
 * Supply either `url` or `blobUrl`, not both. When `blobUrl` is provided, the
 * component fetches through the backend proxy first, then renders the result.
 */
const FileViewerPanel = ({ url: urlProp, blobUrl, fileType, className = '' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedUrl, setResolvedUrl] = useState(urlProp || null);
  const [textContent, setTextContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [excelData, setExcelData] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [excelViewMode, setExcelViewMode] = useState(fileType === 'csv' ? 'table' : 'formatted');
  const [jsonTables, setJsonTables] = useState([]);
  const [selectedJsonTable, setSelectedJsonTable] = useState(0);
  const [jsonViewMode, setJsonViewMode] = useState('json');

  // PDF state
  const [pdfDocument, setPdfDocument] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(0.75);
  const [visiblePage, setVisiblePage] = useState(1);
  const [pageSizes, setPageSizes] = useState([]);
  const canvasRefsMap = useRef({});
  const scrollContainerRef = useRef(null);
  const renderedPagesRef = useRef(new Set());
  const observerRef = useRef(null);
  const renderTasksRef = useRef({});


  // If blobUrl is provided, proxy-fetch it first to get a local object URL
  useEffect(() => {
    if (urlProp) { setResolvedUrl(urlProp); return; }
    if (!blobUrl) return;
    setIsLoading(true);
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const { fetchBlobViaProxy } = await import('../../utils/api');
        const objectUrl = await fetchBlobViaProxy(blobUrl);
        if (!cancelled) setResolvedUrl(objectUrl);
      } catch (err) {
        if (!cancelled) { setError(`Proxy fetch failed: ${err.message}`); setIsLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [urlProp, blobUrl]);

  const url = resolvedUrl;

  // Load file content based on type
  useEffect(() => {
    if (!url) return;
    setIsLoading(true);
    setError(null);

    if (fileType === 'json') {
      fetch(url)
        .then(r => r.ok ? r.text() : Promise.reject(new Error(r.statusText)))
        .then(text => {
          try {
            const parsed = parseJsonViewerContent(text);
            setTextContent(parsed.formatted);
            setJsonTables(parsed.tables);
            setSelectedJsonTable(0);
            setJsonViewMode(parsed.tables.length ? 'table' : 'json');
          }
          catch (_) {
            setTextContent(text);
            setJsonTables([]);
            setSelectedJsonTable(0);
            setJsonViewMode('json');
          }
          setIsLoading(false);
        })
        .catch(err => { setError(err.message); setIsLoading(false); });
    } else if (fileType === 'text') {
      fetch(url)
        .then(r => r.ok ? r.text() : Promise.reject(new Error(r.statusText)))
        .then(text => { setTextContent(text); setIsLoading(false); })
        .catch(err => { setError(err.message); setIsLoading(false); });
    } else if (fileType === 'html') {
      fetch(url)
        .then(r => r.ok ? r.text() : Promise.reject(new Error(r.statusText)))
        .then(html => { setHtmlContent(html); setIsLoading(false); })
        .catch(err => { setError(err.message); setIsLoading(false); });
    } else if (fileType === 'csv') {
      fetch(url)
        .then(r => r.ok ? r.arrayBuffer() : Promise.reject(new Error(r.statusText)))
        .then(buffer => {
          // raw: CSV cells stay the file's own text. Letting SheetJS type them
          // turned "2026-09-17" into a UTC date that rendered a day early
          // (9/16/26) in US timezones, contradicting the imported rows.
          const wb = XLSX.read(buffer, { type: 'array', raw: true });
          setExcelData(wb.SheetNames.map(name => ({
            name,
            data: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, dateNF: 'mm/dd/yyyy', defval: '' })
          })));
          setIsLoading(false);
        })
        .catch(err => { setError(err.message); setIsLoading(false); });
    } else if (fileType === 'excel') {
      fetch(url)
        .then(r => r.ok ? r.arrayBuffer() : Promise.reject(new Error(r.statusText)))
        .then(buffer => {
          const wb = XLSX.read(buffer, { type: 'array', cellStyles: true, cellDates: true, cellNF: true, sheetStubs: true });
          setExcelData(wb.SheetNames.map(name => ({
            name,
            data: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, dateNF: 'mm/dd/yyyy' })
          })));
          setIsLoading(false);
        })
        .catch(err => { setError(err.message); setIsLoading(false); });
    } else if (fileType === 'pdf') {
      (async () => {
        try {
          const pdf = await pdfjsLib.getDocument(url).promise;
          setPdfDocument(pdf);
          setTotalPages(pdf.numPages);
          setVisiblePage(1);
          setIsLoading(false);
        } catch (err) {
          setError(`Failed to load PDF: ${err.message}`);
          setIsLoading(false);
        }
      })();
    }
  }, [url, fileType]);

  // Compute page sizes when PDF loads or scale changes
  useEffect(() => {
    if (!pdfDocument || totalPages === 0) return;
    (async () => {
      const sizes = [];
      for (let i = 1; i <= totalPages; i++) {
        const page = await pdfDocument.getPage(i);
        const vp = page.getViewport({ scale: pdfScale });
        sizes.push({ width: Math.floor(vp.width), height: Math.floor(vp.height) });
      }
      renderedPagesRef.current.clear();
      setPageSizes(sizes);
    })();
  }, [pdfDocument, pdfScale, totalPages]);

  // Render a single PDF page
  const renderPdfPage = async (pageNum) => {
    const canvas = canvasRefsMap.current[pageNum];
    if (!canvas || !pdfDocument || renderedPagesRef.current.has(pageNum)) return;
    renderedPagesRef.current.add(pageNum);
    try {
      const page = await pdfDocument.getPage(pageNum);
      const vp = page.getViewport({ scale: pdfScale });
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d');
      canvas.width = Math.floor(vp.width * dpr);
      canvas.height = Math.floor(vp.height * dpr);
      canvas.style.width = Math.floor(vp.width) + 'px';
      canvas.style.height = Math.floor(vp.height) + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const task = page.render({ canvasContext: ctx, viewport: vp });
      renderTasksRef.current[pageNum] = task;
      await task.promise;
      delete renderTasksRef.current[pageNum];
    } catch (err) {
      if (err.name !== 'RenderingCancelledException') console.error('PDF render error page', pageNum, err);
      renderedPagesRef.current.delete(pageNum);
      delete renderTasksRef.current[pageNum];
    }
  };

  // IntersectionObserver for lazy PDF page rendering
  useEffect(() => {
    if (pageSizes.length === 0 || !scrollContainerRef.current || !pdfDocument) return;
    if (observerRef.current) observerRef.current.disconnect();
    Object.values(renderTasksRef.current).forEach(t => { try { t.cancel(); } catch (_) {} });
    renderTasksRef.current = {};

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          const p = parseInt(e.target.dataset.page);
          if (!isNaN(p)) renderPdfPage(p);
        }
      }),
      { root: scrollContainerRef.current, rootMargin: '500px 0px', threshold: 0 }
    );
    scrollContainerRef.current.querySelectorAll('[data-page]').forEach(el => observer.observe(el));
    observerRef.current = observer;
    return () => {
      observer.disconnect();
      Object.values(renderTasksRef.current).forEach(t => { try { t.cancel(); } catch (_) {} });
    };
  }, [pageSizes, pdfDocument, pdfScale]);

  // Track visible page on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || pageSizes.length === 0) return;
    const onScroll = () => {
      const center = container.scrollTop + container.clientHeight / 2;
      let closest = 1, dist = Infinity;
      container.querySelectorAll('[data-page]').forEach(w => {
        const d = Math.abs(w.offsetTop + w.offsetHeight / 2 - center);
        if (d < dist) { dist = d; closest = parseInt(w.dataset.page); }
      });
      setVisiblePage(closest);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [pageSizes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfDocument) pdfDocument.destroy();
      if (observerRef.current) observerRef.current.disconnect();
      Object.values(renderTasksRef.current).forEach(t => { try { t.cancel(); } catch (_) {} });
    };
  }, [pdfDocument]);

  // In-viewer PDF text search (shared with FileViewerModal) — canvas pages have
  // no text layer, so browser Ctrl+F can't see PDF content.
  const pdfSearch = usePdfSearch({
    pdfDocument, totalPages, pdfScale, scrollContainerRef,
    enabled: fileType === 'pdf',
  });

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mx-auto mb-3" />
          <p className="text-gray-600 text-sm">Loading file...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center max-w-sm p-6">
          <p className="text-red-600 font-medium mb-2">Failed to load file</p>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (fileType === 'json') {
    return (
      <div className={`w-full h-full flex flex-col bg-slate-950 overflow-hidden ${className}`}>
        <div className="flex-shrink-0 border-b border-slate-800 bg-slate-900 px-3 py-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-200">
              <FileText className="h-3.5 w-3.5" />
              <span>JSON Preview</span>
            </div>
            <div className="flex items-center gap-1.5">
              {jsonTables.length > 0 && (
                <select
                  value={selectedJsonTable}
                  onChange={(event) => setSelectedJsonTable(Number(event.target.value))}
                  className="h-7 max-w-[220px] rounded border border-slate-700 bg-slate-950 px-2 text-[11px] text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  title="JSON table"
                >
                  {jsonTables.map((table, index) => (
                    <option key={`${table.name}-${index}`} value={index}>
                      {table.name} ({table.sourceRowCount}{table.truncated ? '+' : ''})
                    </option>
                  ))}
                </select>
              )}
              <div className="inline-flex rounded border border-slate-700 overflow-hidden">
                <button
                  onClick={() => setJsonViewMode('json')}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium ${
                    jsonViewMode === 'json' ? 'bg-blue-600 text-white' : 'bg-slate-950 text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  <FileText className="h-3 w-3" />
                  JSON
                </button>
                <button
                  onClick={() => setJsonViewMode('table')}
                  disabled={jsonTables.length === 0}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                    jsonViewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-slate-950 text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  <Grid className="h-3 w-3" />
                  Tables
                </button>
              </div>
            </div>
          </div>
        </div>
        {jsonViewMode === 'table' && jsonTables.length > 0 ? (
          <div className="flex-1 min-h-0 bg-white">
            <ExcelViewer
              data={jsonTables}
              selectedSheet={selectedJsonTable}
              onSheetChange={setSelectedJsonTable}
            />
          </div>
        ) : (
          <pre className="flex-1 min-h-0 p-4 overflow-auto text-sm leading-6 text-emerald-100 font-mono whitespace-pre-wrap break-words">
            {textContent}
          </pre>
        )}
      </div>
    );
  }

  if (fileType === 'text') {
    return (
      <pre className={`w-full h-full p-4 overflow-auto bg-gray-50 text-sm text-gray-800 whitespace-pre-wrap break-words ${className}`}>
        {textContent}
      </pre>
    );
  }

  if (fileType === 'html') {
    return (
      <div className={`w-full h-full overflow-hidden bg-white ${className}`}>
        <iframe srcDoc={htmlContent} className="w-full h-full border-none" title="HTML Viewer"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms" style={{ background: 'white' }} />
      </div>
    );
  }

  if (fileType === 'excel' || fileType === 'csv') {
    return (
      <div className={`w-full h-full flex flex-col bg-white overflow-hidden ${className}`}>
        <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-1.5">
          <div className="flex items-center justify-between">
            {excelData && excelData.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto">
                {excelData.map((sheet, i) => (
                  <button key={i} onClick={() => setSelectedSheet(i)}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                      selectedSheet === i ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}>{sheet.name}</button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-600">View:</span>
              {fileType === 'excel' && (
                <button onClick={() => setExcelViewMode('formatted')}
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    excelViewMode === 'formatted' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}><FileText className="inline h-3 w-3 mr-0.5" />Formatted</button>
              )}
              <button onClick={() => setExcelViewMode('table')}
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  excelViewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}><Grid className="inline h-3 w-3 mr-0.5" />Table</button>
            </div>
          </div>
        </div>
        <div className="flex-grow relative">
          {excelViewMode === 'formatted' && fileType === 'excel' ? (
            <ExcelViewerFormatted url={url} fileName="Excel File" />
          ) : excelData ? (
            <ExcelViewer data={excelData} selectedSheet={selectedSheet} onSheetChange={setSelectedSheet} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // PDF
  return (
    <div className={`w-full h-full flex flex-col bg-gray-100 relative ${className}`}>
      {/* Search + zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
        <PdfSearchControl search={pdfSearch} />
        <div className="flex items-center gap-1 bg-white/90 backdrop-blur rounded-lg shadow px-2 py-1">
          <button onClick={() => setPdfScale(s => Math.max(0.5, s - 0.25))} disabled={pdfScale <= 0.5}
            className="p-1 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100 disabled:opacity-50" title="Zoom out">
            <ZoomOut size={16} />
          </button>
          <span className="text-xs text-gray-600 min-w-[40px] text-center">{Math.round(pdfScale * 100)}%</span>
          <button onClick={() => setPdfScale(s => Math.min(3, s + 0.25))} disabled={pdfScale >= 3}
            className="p-1 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100 disabled:opacity-50" title="Zoom in">
            <ZoomIn size={16} />
          </button>
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-grow overflow-auto">
        {pdfDocument && pageSizes.length > 0 ? (
          <div
            className="flex flex-col items-center py-4 gap-4"
            style={{ minWidth: '100%', width: 'max-content' }}
          >
            {pageSizes.map((size, i) => (
              <div key={i + 1} data-page={i + 1}
                style={{ width: size.width, height: size.height }}
                className="flex-shrink-0 shadow-lg bg-white relative">
                <canvas ref={el => { if (el) canvasRefsMap.current[i + 1] = el; }} style={{ display: 'block' }} />
                <PdfPageHighlights search={pdfSearch} page={i + 1} scale={pdfScale} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" />
          </div>
        )}
      </div>

      {totalPages > 0 && pageSizes.length > 0 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full pointer-events-none">
          Page {visiblePage} of {totalPages}
        </div>
      )}
    </div>
  );
};

export default FileViewerPanel;
